// frontend/src/components/GanttView.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Form, Alert, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import Gantt from 'frappe-gantt';
import api from '../api';

const STATUT_COLORS = {
  planifie: '#6c757d',
  en_cours: '#0d6efd',
  termine:  '#198754',
  bloque:   '#dc3545',
};

const STATUT_LABELS = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  termine:  'Terminé',
  bloque:   'Bloqué',
};

const EMPTY_TACHE = {
  nom: '',
  description: '',
  statut: 'planifie',
  date_debut: '',
  date_fin: '',
  avancement: 0,
  depends_on: '',
};

function GanttView({ chantier, onClose }) {
  const ganttRef        = useRef(null);
  const ganttInstance   = useRef(null);
  const [taches, setTaches]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [editTache, setEditTache]   = useState(null);
  const [form, setForm]             = useState(EMPTY_TACHE);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode]     = useState('Week');

  const fetchTaches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/chantiers/${chantier.id}/taches`);
      setTaches(res.data);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [chantier.id]);

  useEffect(() => {
    fetchTaches();
  }, [fetchTaches]);

  useEffect(() => {
    if (!loading && taches.length > 0 && ganttRef.current) {
      // Convertir les tâches au format frappe-gantt
      const tasks = taches.map(t => ({
        id:           String(t.id),
        name:         t.nom,
        start:        t.date_debut,
        end:          t.date_fin,
        progress:     t.avancement || 0,
        dependencies: t.depends_on ? String(t.depends_on) : '',
        custom_class: `statut-${t.statut}`,
      }));

      // Détruire l'instance précédente
      if (ganttInstance.current) {
        ganttRef.current.innerHTML = '';
      }

      ganttInstance.current = new Gantt(ganttRef.current, tasks, {
        view_mode: viewMode,
        language: 'fr',
        on_click: (task) => {
          const tache = taches.find(t => String(t.id) === task.id);
          if (tache) openEditModal(tache);
        },
        on_date_change: async (task, start, end) => {
          try {
            await api.put(`/chantiers/${chantier.id}/taches/${task.id}`, {
              date_debut: start.toISOString().substring(0, 10),
              date_fin:   end.toISOString().substring(0, 10),
            });
            fetchTaches();
          } catch (err) {
            setError('Erreur lors de la mise à jour des dates');
          }
        },
        on_progress_change: async (task, progress) => {
          try {
            await api.put(`/chantiers/${chantier.id}/taches/${task.id}`, {
              avancement: Math.round(progress),
            });
            fetchTaches();
          } catch (err) {
            setError('Erreur lors de la mise à jour de l\'avancement');
          }
        },
      });
    }
  }, [taches, loading, viewMode, chantier.id]);

  // Changer le mode de vue
  useEffect(() => {
    if (ganttInstance.current) {
      ganttInstance.current.change_view_mode(viewMode);
    }
  }, [viewMode]);

  const openCreateModal = () => {
    setEditTache(null);
    setForm(EMPTY_TACHE);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (tache) => {
    setEditTache(tache);
    setForm({
      nom:         tache.nom,
      description: tache.description || '',
      statut:      tache.statut,
      date_debut:  tache.date_debut || '',
      date_fin:    tache.date_fin || '',
      avancement:  tache.avancement || 0,
      depends_on:  tache.depends_on || '',
    });
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      ...form,
      avancement: parseInt(form.avancement) || 0,
      depends_on: form.depends_on ? parseInt(form.depends_on) : null,
    };

    try {
      if (editTache) {
        await api.put(`/chantiers/${chantier.id}/taches/${editTache.id}`, payload);
      } else {
        await api.post(`/chantiers/${chantier.id}/taches`, payload);
      }
      setShowModal(false);
      await fetchTaches();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editTache || !window.confirm('Supprimer cette tâche ?')) return;
    try {
      await api.delete(`/chantiers/${chantier.id}/taches/${editTache.id}`);
      setShowModal(false);
      await fetchTaches();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const avancementGlobal = taches.length > 0
    ? Math.round(taches.reduce((sum, t) => sum + (t.avancement || 0), 0) / taches.length)
    : 0;

  return (
    <div style={{ padding: '0' }}>
      {/* En-tête */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white',
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: '8px 8px 0 0',
        marginBottom: '16px',
      }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700 }}>🏗️ {chantier.nom}</h4>
          <small style={{ opacity: 0.8 }}>
            {chantier.date_debut} → {chantier.date_fin_prevue} · Budget : {chantier.budget ? `${Number(chantier.budget).toLocaleString('fr-FR')} €` : 'N/A'}
          </small>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px' }}>AVANCEMENT GLOBAL</div>
            <div style={{ width: '160px' }}>
              <ProgressBar
                now={avancementGlobal}
                label={`${avancementGlobal}%`}
                style={{ height: '20px', borderRadius: '10px' }}
                variant={avancementGlobal === 100 ? 'success' : avancementGlobal > 50 ? 'info' : 'warning'}
              />
            </div>
          </div>
          <Button variant="outline-light" size="sm" onClick={onClose}>✕ Fermer</Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Barre d'outils */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '0 4px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['Day', 'Week', 'Month', 'Quarter Day'].map(mode => (
            <Button
              key={mode}
              size="sm"
              variant={viewMode === mode ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode(mode)}
            >
              {mode === 'Quarter Day' ? 'Quart jour' : mode === 'Day' ? 'Jour' : mode === 'Week' ? 'Semaine' : 'Mois'}
            </Button>
          ))}
        </div>
        <Button variant="success" size="sm" onClick={openCreateModal}>
          + Nouvelle tâche
        </Button>
      </div>

      {/* Gantt */}
      {loading ? (
        <div className="text-center mt-5 mb-5">
          <Spinner animation="border" />
          <p className="mt-2">Chargement du planning...</p>
        </div>
      ) : taches.length === 0 ? (
        <Alert variant="info">
          Aucune tâche pour ce chantier. <Button variant="link" onClick={openCreateModal}>Créer la première tâche</Button>
        </Alert>
      ) : (
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '16px',
        }}>
          <style>{`
            .gantt .bar-progress { fill: #0d6efd; }
            .gantt .bar { fill: #e9ecef; }
            .gantt .bar-label { fill: #333; font-size: 12px; }
            .statut-termine .bar-progress { fill: #198754; }
            .statut-bloque .bar-progress { fill: #dc3545; }
            .statut-en_cours .bar-progress { fill: #0d6efd; }
            .statut-planifie .bar-progress { fill: #6c757d; }
          `}</style>
          <div ref={ganttRef} />
        </div>
      )}

      {/* Liste des tâches */}
      {taches.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h6 style={{ color: '#666', marginBottom: '8px' }}>TÂCHES ({taches.length})</h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {taches.map(t => (
              <div key={t.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 14px',
                background: '#f8f9fa',
                borderRadius: '6px',
                borderLeft: `4px solid ${STATUT_COLORS[t.statut]}`,
                cursor: 'pointer',
              }} onClick={() => openEditModal(t)}>
                <div style={{ flex: 1 }}>
                  <strong>{t.nom}</strong>
                  {t.depends_on && <small style={{ color: '#999', marginLeft: '8px' }}>↳ dépend de #{t.depends_on}</small>}
                  <div style={{ fontSize: '11px', color: '#999' }}>
                    {t.date_debut} → {t.date_fin}
                    {t.responsable && ` · 👤 ${t.responsable}`}
                  </div>
                </div>
                <Badge style={{ background: STATUT_COLORS[t.statut] }}>
                  {STATUT_LABELS[t.statut]}
                </Badge>
                <div style={{ width: '100px' }}>
                  <ProgressBar
                    now={t.avancement}
                    label={`${t.avancement}%`}
                    style={{ height: '14px' }}
                    variant={t.avancement === 100 ? 'success' : 'primary'}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal tâche */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editTache ? '✏️ Modifier la tâche' : '➕ Nouvelle tâche'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Nom de la tâche *</Form.Label>
              <Form.Control type="text" name="nom" required value={form.nom} onChange={handleField} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={form.description} onChange={handleField} />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date de début *</Form.Label>
                  <Form.Control type="date" name="date_debut" required value={form.date_debut} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date de fin *</Form.Label>
                  <Form.Control type="date" name="date_fin" required value={form.date_fin} onChange={handleField} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Statut</Form.Label>
                  <Form.Select name="statut" value={form.statut} onChange={handleField}>
                    {Object.entries(STATUT_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Avancement ({form.avancement}%)</Form.Label>
                  <Form.Range name="avancement" min="0" max="100" value={form.avancement} onChange={handleField} />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Dépend de (ID tâche)</Form.Label>
              <Form.Select name="depends_on" value={form.depends_on} onChange={handleField}>
                <option value="">Aucune dépendance</option>
                {taches.filter(t => !editTache || t.id !== editTache.id).map(t => (
                  <option key={t.id} value={t.id}>#{t.id} — {t.nom}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            {editTache && (
              <Button variant="danger" className="me-auto" onClick={handleDelete}>
                🗑️ Supprimer
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Enregistrement...' : (editTache ? 'Mettre à jour' : 'Créer')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default GanttView;
